const PATH_SEPARATORS = /[\\/]+/gu;
const INVALID_FILENAME_CHARACTERS = /[<>:"|?*]/gu;
const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\..*)?$/iu;

function truncateFileName(name: string, maxLength: number): string {
  if (name.length <= maxLength) {
    return name;
  }

  const extensionIndex = name.lastIndexOf(".");
  const hasExtension = extensionIndex > 0 && extensionIndex < name.length - 1;
  const extension = hasExtension ? name.slice(extensionIndex) : "";
  const stem = hasExtension ? name.slice(0, extensionIndex) : name;
  const availableStemLength = Math.max(1, maxLength - extension.length);
  return `${stem.slice(0, availableStemLength)}${extension}`.slice(0, maxLength);
}

function stripControlCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint >= 32 && codePoint !== 127;
    })
    .join("");
}

export function sanitizeFileName(input: string, maxLength = 120): string {
  const leaf = stripControlCharacters(
    input.normalize("NFC").replace(PATH_SEPARATORS, "_"),
  )
    .replace(INVALID_FILENAME_CHARACTERS, "_")
    .replace(/\s+/gu, " ")
    .replace(/^\.+/u, "")
    .replace(/[ .]+$/u, "")
    .trim();

  let safeName = leaf || "file";
  if (safeName === "." || safeName === "..") {
    safeName = "file";
  }
  if (WINDOWS_RESERVED_NAME.test(safeName)) {
    safeName = `_${safeName}`;
  }

  return truncateFileName(safeName, maxLength);
}

function splitExtension(name: string): { stem: string; extension: string } {
  const extensionIndex = name.lastIndexOf(".");
  if (extensionIndex <= 0 || extensionIndex === name.length - 1) {
    return { stem: name, extension: "" };
  }
  return {
    stem: name.slice(0, extensionIndex),
    extension: name.slice(extensionIndex),
  };
}

export function sanitizeAndDeduplicateFileNames(
  inputs: readonly string[],
  maxLength = 120,
): string[] {
  const used = new Set<string>();

  return inputs.map((input) => {
    const sanitized = sanitizeFileName(input, maxLength);
    const { stem, extension } = splitExtension(sanitized);
    let candidate = sanitized;
    let suffix = 2;

    while (used.has(candidate.toLocaleLowerCase("en-US"))) {
      const marker = ` (${suffix})`;
      const allowedStem = Math.max(1, maxLength - extension.length - marker.length);
      candidate = `${stem.slice(0, allowedStem)}${marker}${extension}`;
      suffix += 1;
    }

    used.add(candidate.toLocaleLowerCase("en-US"));
    return candidate;
  });
}
